<template>
  <div class="min-h-screen bg-white">
    <Head :title="title">
      <meta name="description" :content="description" />
      <meta property="og:title" :content="title" />
      <meta property="og:description" :content="description" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="INSATEQ" />
    </Head>

    <!-- Header -->
    <header class="bg-white border-b border-gray-100 sticky top-0 z-50">
      <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo -->
          <a :href="route('home')" class="flex items-center">
            <img
              src="/images/insateq-logo-dark.svg"
              alt="INSATEQ"
              class="h-10 w-auto"
            />
          </a>

          <!-- Navigation -->
          <div class="hidden md:flex items-center space-x-6">
            <a :href="route('home')" class="text-sm text-gray-600 hover:text-gray-900 transition">Ana Sayfa</a>
            <a :href="route('pricing')" class="text-sm text-gray-600 hover:text-gray-900 transition">Fiyatlandırma</a>
            <a :href="route('contact')" class="text-sm text-gray-600 hover:text-gray-900 transition">İletişim</a>
          </div>

          <!-- CTA -->
          <div class="flex items-center space-x-4">
            <a :href="route('login')" class="text-sm text-gray-600 hover:text-gray-900 transition font-medium">
              Giriş Yap
            </a>
            <a
              :href="route('register')"
              class="hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all"
            >
              Ücretsiz Dene
            </a>
          </div>
        </div>
      </nav>
    </header>

    <!-- Main Content -->
    <main>
      <slot />
    </main>

    <!-- Footer -->
    <footer class="bg-slate-900 text-white mt-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
          <!-- Brand -->
          <div class="col-span-2 md:col-span-1">
            <a href="/" class="flex items-center mb-4">
              <img
                src="/images/insateq-logo-light.svg"
                alt="INSATEQ"
                class="h-10 w-auto"
              />
            </a>
            <p class="text-gray-400 text-sm">
              Türkiye'nin lider inşaat yönetim platformu.
            </p>
          </div>

          <!-- Product -->
          <div>
            <h4 class="text-white font-semibold mb-4">Ürün</h4>
            <ul class="space-y-2">
              <li><a :href="route('home') + '#features'" class="text-gray-400 hover:text-white text-sm transition-colors">Özellikler</a></li>
              <li><a :href="route('pricing')" class="text-gray-400 hover:text-white text-sm transition-colors">Fiyatlandırma</a></li>
              <li><a :href="route('changelog')" class="text-gray-400 hover:text-white text-sm transition-colors">Changelog</a></li>
            </ul>
          </div>

          <!-- Support -->
          <div>
            <h4 class="text-white font-semibold mb-4">Destek</h4>
            <ul class="space-y-2">
              <li><a :href="route('contact')" class="text-gray-400 hover:text-white text-sm transition-colors">İletişim</a></li>
              <li><a href="mailto:destek@insateq.com" class="text-gray-400 hover:text-white text-sm transition-colors">destek@insateq.com</a></li>
            </ul>
          </div>

          <!-- Legal -->
          <div>
            <h4 class="text-white font-semibold mb-4">Yasal</h4>
            <ul class="space-y-2">
              <li><a :href="route('terms')" class="text-gray-400 hover:text-white text-sm transition-colors">Kullanım Şartları</a></li>
              <li><a :href="route('privacy')" class="text-gray-400 hover:text-white text-sm transition-colors">Gizlilik Politikası</a></li>
            </ul>
          </div>
        </div>

        <!-- Eğitim Çözüm Ortağı -->
        <div class="border-t border-slate-800 mt-8 pt-8 pb-4">
          <div class="flex flex-col items-center space-y-3">
            <span class="text-xs font-semibold uppercase tracking-widest text-gray-500">Eğitim Çözüm Ortağımız</span>
            <a
              href="https://ekurumsalgelisim.com?ref=insateq"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center space-x-2.5 px-6 py-3 rounded-xl bg-emerald-950/50 border border-emerald-800/40 text-emerald-400 hover:bg-emerald-900/50 hover:border-emerald-700/50 hover:text-emerald-300 transition-all"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
              </svg>
              <span class="font-semibold text-sm">eKurumsal Gelişim</span>
              <svg class="h-3.5 w-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
          </div>
        </div>

        <div class="border-t border-slate-800 mt-4 pt-6">
          <p class="text-center text-gray-400 text-sm">
            &copy; {{ currentYear }} INSATEQ. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { Head } from '@inertiajs/vue3'

defineProps({
  title: {
    type: String,
    required: true
  },
  description: {
    type: String,
    default: ''
  }
})

const currentYear = new Date().getFullYear()
</script>
